import axiosInstance from '../../../../lib/axios';
import type {
  Campaign,
  CampaignInput,
  CampaignStats,
  CampaignStatus,
  Coupon,
  CouponCreateInput,
  IssuedCoupon,
  PageResult,
  Redemption,
  TargetInput,
  CampaignTarget,
} from '../types';

const BASE = '/v1/admin/campaigns';
type Envelope<T> = { data: { data: T } };

export interface CampaignListParams {
  status?: CampaignStatus | '';
  search?: string;
  page: number;
  pageSize: number;
}

export const listCampaigns = async (params: CampaignListParams) => {
  const response: Envelope<PageResult<Campaign>> = await axiosInstance.get(BASE, {
    params: { ...params, status: params.status || undefined },
  });
  return response.data.data;
};

export const getCampaign = async (publicId: string) => {
  const response: Envelope<Campaign> = await axiosInstance.get(`${BASE}/${publicId}`);
  return response.data.data;
};

export const createCampaign = async (input: CampaignInput) => {
  const response: Envelope<Campaign> = await axiosInstance.post(BASE, input);
  return response.data.data;
};

export const patchCampaign = async (publicId: string, input: Partial<CampaignInput>) => {
  const response: Envelope<Campaign> = await axiosInstance.patch(`${BASE}/${publicId}`, input);
  return response.data.data;
};

export const setCampaignStatus = async (publicId: string, action: 'activate' | 'pause') => {
  const response: Envelope<Campaign> = await axiosInstance.post(`${BASE}/${publicId}/${action}`);
  return response.data.data;
};

export const replaceCampaignTargets = async (publicId: string, targets: TargetInput[]) => {
  const response: Envelope<CampaignTarget[]> = await axiosInstance.put(
    `${BASE}/${publicId}/targets`,
    { targets }
  );
  return response.data.data;
};

export const listCoupons = async (publicId: string, page: number, pageSize: number) => {
  const response: Envelope<PageResult<Coupon>> = await axiosInstance.get(
    `${BASE}/${publicId}/coupons`,
    { params: { page, pageSize } }
  );
  return response.data.data;
};

export const createCoupons = async (publicId: string, input: CouponCreateInput) => {
  const response: Envelope<Pick<Coupon, 'publicId' | 'code'>[]> = await axiosInstance.post(
    `${BASE}/${publicId}/coupons`,
    input
  );
  return response.data.data;
};

export const patchCoupon = async (
  publicId: string,
  input: { isActive?: boolean; maxUsage?: number | null }
) => {
  const response: Envelope<Coupon> = await axiosInstance.patch(
    `${BASE}/coupons/${publicId}`,
    input
  );
  return response.data.data;
};

export const exportCouponsCsv = async (publicId: string) => {
  const response = await axiosInstance.get<string>(`${BASE}/${publicId}/coupons/export`, {
    responseType: 'text',
  });
  return response.data;
};

export const listRedemptions = async (publicId: string, page: number, pageSize: number) => {
  const response: Envelope<PageResult<Redemption>> = await axiosInstance.get(
    `${BASE}/${publicId}/redemptions`,
    { params: { page, pageSize } }
  );
  return response.data.data;
};

export const getCampaignStats = async (publicId: string) => {
  const response: Envelope<CampaignStats> = await axiosInstance.get(`${BASE}/${publicId}/stats`);
  return response.data.data;
};

export const listIssuedCoupons = async (publicId: string, page: number, pageSize: number) => {
  const response: Envelope<PageResult<IssuedCoupon>> = await axiosInstance.get(
    `${BASE}/${publicId}/issued`,
    { params: { page, pageSize } }
  );
  return response.data.data;
};
